import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getUserProfile } from '../firebase/firebaseFirestore';

interface UserProfile {
  nom?: string;
  prenom?: string;
  username?: string;
  adresse?: string;
  ville?: string;
  pays?: string;
  email?: string;
  role?: string;
  isAdmin?: boolean;
}

interface UserProfileContextType {
  profile: UserProfile | null;
  updateProfile: (newProfile: UserProfile) => void;
  refreshProfile: () => Promise<void>;
  loading: boolean;
}

const UserProfileContext = createContext<UserProfileContextType>({
  profile: null,
  updateProfile: () => {},
  refreshProfile: async () => {},
  loading: true
});

export const useUserProfile = () => useContext(UserProfileContext);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    if (!user || !user.email) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      const result = await getUserProfile(user.email);
      if (result.success && result.data) {
        setProfile(result.data as UserProfile);
      } else {
        setProfile({ email: user.email || '' });
      }
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      setProfile({ email: user.email || '' });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadProfile();
  }, [user?.email, loadProfile]); // Inclure loadProfile dans les dépendances

  const updateProfile = useCallback((newProfile: UserProfile) => {
    setProfile(newProfile);
  }, []);

  const refreshProfile = useCallback(async () => {
    await loadProfile();
  }, [loadProfile]);

  const value = useMemo(() => ({
    profile,
    updateProfile,
    refreshProfile,
    loading
  }), [profile, updateProfile, refreshProfile, loading]);

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};
